import { useEffect, useState, type ReactElement } from 'react';
import type { BackupDto } from '@gr/core/platform/operations/backups';
import { isTaskActive, TaskKind, type TaskDto } from '@gr/core/platform/tasks/dto';
import { useServices, useT } from '../../../app/di';
import { formatDateTime } from '../../../shared/format';
import { useAction, useResource } from '../../../shared/hooks';
import { useObservable } from '../../../shared/store';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Dropzone } from '../../../ui-kit/Dropzone/Dropzone';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { ProgressBar } from '../../../ui-kit/ProgressBar/ProgressBar';
import { TaskCard, useTaskActions } from '../tasks';
import { BackupList } from './BackupList/BackupList';
import { BackupTaskSummary } from './BackupTaskSummary/BackupTaskSummary';

const BACKUP_KINDS: readonly TaskKind[] = [TaskKind.Backup, TaskKind.Restore];

interface Upload {
  name: string;
  fraction: number;
}

// OPS-UC-8…11, OPS-RULE-2, DEMO-RULE-6: резервные копии платформы — создание, загрузка,
// скачивание, восстановление, удаление. Ход задач копий приходит по соединению событий.
export function AdminBackupsPage(): ReactElement {
  const { api, tasks } = useServices();
  const { t, te, locale } = useT();
  const confirm = useConfirm();
  const info = useResource(() => api.operations.info(), [api]);
  const backups = useResource(() => api.operations.backups(), [api]);
  const loadedTasks = useResource(() => api.tasks.listAll(), [api]);
  const all = useObservable(tasks.tasks);
  const taskActions = useTaskActions();
  const creating = useAction(() => api.operations.createBackup());
  const restoring = useAction((name: string) => api.operations.restoreBackup(name));
  const deleting = useAction((name: string) => api.operations.deleteBackup(name));
  const [upload, setUpload] = useState<Upload | null>(null);
  const [uploadError, setUploadError] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const demo = info.data?.demo ?? false;

  useEffect(() => {
    if (loadedTasks.data) tasks.merge(loadedTasks.data);
  }, [loadedTasks.data, tasks]);

  // Последняя задача копий: идущая или завершённая недавно — с итогом.
  const backupTasks = [...all.values()].filter((task) => BACKUP_KINDS.includes(task.kind)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const latest = backupTasks[0] ?? null;
  const busy = backupTasks.some((task) => isTaskActive(task.status));
  // Задача копий завершилась — список изменился.
  const lastFinished = backupTasks.reduce((at, task) => (task.finishedAt && task.finishedAt > at ? task.finishedAt : at), '');
  const { reload } = backups;
  useEffect(() => {
    if (lastFinished) void reload();
  }, [lastFinished, reload]);

  const started = (result: { success: boolean; data: TaskDto | null }): void => {
    if (result.success && result.data) tasks.merge([result.data]);
  };

  const onCreate = async (): Promise<void> => {
    setNotice(null);
    started(await creating.run());
  };

  const onUpload = async (files: File[]): Promise<void> => {
    const file = files[0];
    if (!file) return;
    setNotice(null);
    setUploadError(null);
    // DEMO-RULE-6: в демо файл не отправляется — сервер всё равно его не сохранит.
    if (demo) {
      setNotice(t('operations.backups.uploadedDemo'));
      return;
    }
    setUpload({ name: file.name, fraction: 0 });
    const result = await api.operations.uploadBackup(file, (progress) => setUpload({ name: file.name, fraction: progress.total > 0 ? progress.loaded / progress.total : 0 }));
    setUpload(null);
    if (!result.success) {
      setUploadError(result.messages);
      return;
    }
    if (result.data.backup) setNotice(t('operations.backups.uploaded', { name: result.data.backup.name }));
    await reload();
  };

  const onRestore = async (backup: BackupDto): Promise<void> => {
    const date = formatDateTime(backup.summary?.createdAt ?? backup.addedAt, locale);
    if (!(await confirm({ title: t('operations.backups.restoreConfirm', { name: backup.name }), text: t('operations.backups.restoreText', { date }), confirmLabel: t('operations.backups.restore'), danger: true }))) return;
    setNotice(null);
    started(await restoring.run(backup.name));
  };

  const onDelete = async (backup: BackupDto): Promise<void> => {
    if (!(await confirm({ title: t('operations.backups.deleteConfirm', { name: backup.name }), text: t('operations.backups.deleteText'), confirmLabel: t('operations.backups.delete'), danger: true }))) return;
    if ((await deleting.run(backup.name)).success) await reload();
  };

  const error = creating.error ?? restoring.error ?? deleting.error ?? uploadError ?? taskActions.error ?? backups.error;
  return (
    <div className="u-stack-lg">
      <PageHeader title={t('operations.backups.title')} subtitle={t('operations.backups.subtitle')} />
      {demo ? <Alert tone="info">{t('operations.backups.demo')}</Alert> : null}
      {error ? <Alert tone="danger">{te(error)}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      <Card title={t('operations.backups.newTitle')}>
        <div className="u-stack">
          <p className="u-muted u-small">{t('operations.backups.contents')}</p>
          {busy ? <Alert tone="info">{t('operations.backups.busy')}</Alert> : null}
          <div>
            <Button variant="primary" icon="archive" disabled={busy} loading={creating.pending} onClick={() => void onCreate()}>
              {t('operations.backups.create')}
            </Button>
          </div>
          {latest ? (
            <TaskCard
              task={latest}
              canManage={taskActions.canManage(latest)}
              pending={taskActions.pendingId === latest.id}
              onCancel={(task) => void taskActions.cancel(task)}
              onResume={(task) => void taskActions.resume(task)}
            >
              <BackupTaskSummary task={latest} />
            </TaskCard>
          ) : null}
          {upload ? (
            <div className="u-stack u-gap-1">
              <p className="u-small">{t('operations.backups.uploading', { name: upload.name, percent: Math.round(upload.fraction * 100) })}</p>
              <ProgressBar value={upload.fraction} label={t('operations.backups.uploading', { name: upload.name, percent: Math.round(upload.fraction * 100) })} />
            </div>
          ) : (
            <Dropzone accept=".zip,application/zip" label={t('operations.backups.upload')} hint={t('operations.backups.uploadHint')} disabled={busy} onFiles={(files) => void onUpload(files)} />
          )}
        </div>
      </Card>
      <Card title={t('operations.backups.listTitle')}>
        {backups.loading && !backups.data ? (
          <Loading label={t('common.loading')} />
        ) : (
          <BackupList
            backups={backups.data ?? []}
            downloadUrl={(name) => api.operations.downloadUrl(name)}
            busy={busy || restoring.pending}
            pendingName={null}
            onRestore={(backup) => void onRestore(backup)}
            onDelete={(backup) => void onDelete(backup)}
          />
        )}
      </Card>
    </div>
  );
}
